<script>
import api from '@/helpers/api'
import moment from 'moment'
import {
  periodCounts,
  metricTrends,
  withDevCounts,
  isDashboardDev
} from '@/router/views/dashboards/shared/dev-data'

export default {
  name: 'stats-period-metrics',
  props: {
    year: {
      type: Number,
      default: () => moment().year()
    }
  },
  data () {
    return {
      monthCount: 0,
      yearCount: 0,
      monthTrend: 0,
      yearTrend: 0,
      isLoading: false
    }
  },
  computed: {
    isCurrentYear () {
      return this.year === moment().year()
    }
  },
  watch: {
    year: {
      immediate: true,
      handler: 'onRefresh'
    }
  },
  methods: {
    async onRefresh () {
      this.isLoading = true

      const yearStart = moment()
        .year(this.year)
        .startOf('year')
        .toISOString()
      const yearEnd = this.isCurrentYear
        ? moment().toISOString()
        : moment()
            .year(this.year)
            .endOf('year')
            .toISOString()
      const prevYearStart = moment()
        .year(this.year - 1)
        .startOf('year')
        .toISOString()
      const prevYearEnd = this.isCurrentYear
        ? moment().subtract(1, 'year').toISOString()
        : moment()
            .year(this.year - 1)
            .endOf('year')
            .toISOString()

      let month = 0
      let prevMonth = 0
      if (this.isCurrentYear) {
        const monthStart = moment().startOf('month').toISOString()
        const monthEnd = moment().endOf('month').toISOString()
        const prevMonthStart = moment()
          .subtract(1, 'month')
          .startOf('month')
          .toISOString()
        const prevMonthEnd = moment()
          .subtract(1, 'month')
          .endOf('month')
          .toISOString()
        ;[month, prevMonth] = await Promise.all([
          this.countByPeriod(monthStart, monthEnd),
          this.countByPeriod(prevMonthStart, prevMonthEnd)
        ])
      }

      const [year, prevYear] = await Promise.all([
        this.countByPeriod(yearStart, yearEnd),
        this.countByPeriod(prevYearStart, prevYearEnd)
      ])

      const periods = withDevCounts(
        { month, year },
        { month: periodCounts.month, year: periodCounts.year }
      )
      const usingFallback =
        isDashboardDev() && month === 0 && year === 0

      this.monthCount = this.isCurrentYear ? periods.month : 0
      this.yearCount = periods.year

      if (usingFallback) {
        this.monthTrend = metricTrends.month
        this.yearTrend = metricTrends.year
      } else {
        this.monthTrend = this.percentChange(month, prevMonth)
        this.yearTrend = this.percentChange(year, prevYear)
      }

      this.isLoading = false
    },
    async countByPeriod (start, end) {
      try {
        const { count } = await api.webForms.countWhere({
          filters: [
            { is_submitted: { _eq: true } },
            { submitted_at: { _gte: start, _lte: end } }
          ]
        })
        return count
      } catch (e) {
        return 0
      }
    },
    percentChange (current, previous) {
      if (!previous) return current > 0 ? 100 : 0
      return Math.round(((current - previous) / previous) * 100)
    },
    formatTrend (value) {
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}%`
    },
    goToOrders () {
      this.$router.push({ name: 'sales.my-orders' })
    }
  }
}
</script>

<template>
  <div
    class="portal-grid portal-grid--metrics mb-2"
    :class="{ 'portal-grid--metrics-single': !isCurrentYear }"
  >
    <button
      v-if="isCurrentYear"
      type="button"
      class="portal-card portal-card--metric"
      :disabled="isLoading"
      @click="goToOrders"
    >
      <div class="portal-card__eyebrow">
        {{ $t('dashboard.stats.orders_month') }}
      </div>
      <div class="portal-card__value">{{ monthCount }}</div>
      <div
        class="portal-card__trend"
        :class="monthTrend >= 0 ? 'is-up' : 'is-down'"
      >
        <i
          :class="
            monthTrend >= 0 ? 'mdi mdi-trending-up' : 'mdi mdi-trending-down'
          "
        ></i>
        {{ formatTrend(monthTrend) }}
        <span>{{ $t('dashboard.stats.vs_last_month') }}</span>
      </div>
    </button>

    <button
      type="button"
      class="portal-card portal-card--metric"
      :disabled="isLoading"
      @click="goToOrders"
    >
      <div class="portal-card__eyebrow">
        {{
          isCurrentYear
            ? $t('dashboard.stats.orders_year')
            : $t('stats.metrics.orders_in_year', { year })
        }}
      </div>
      <div class="portal-card__value">{{ yearCount }}</div>
      <div
        class="portal-card__trend"
        :class="yearTrend >= 0 ? 'is-up' : 'is-down'"
      >
        <i
          :class="
            yearTrend >= 0 ? 'mdi mdi-trending-up' : 'mdi mdi-trending-down'
          "
        ></i>
        {{ formatTrend(yearTrend) }}
        <span>{{ $t('dashboard.stats.vs_last_year') }}</span>
      </div>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.portal-grid--metrics {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  &.portal-grid--metrics-single {
    @media (min-width: 768px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

.portal-card--metric {
  appearance: none;
  border: 0;
  text-align: left;
  background: #fff;
  border-radius: 0.85rem;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  cursor: pointer;
  width: 100%;
  min-width: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.07);
  }

  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
}

.portal-card__eyebrow {
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 500;
  margin-bottom: 0.2rem;
}

.portal-card__value {
  color: #0f172a;
  font-size: 1.45rem;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.portal-card__trend {
  margin-top: 0.35rem;
  font-size: 0.72rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.2rem;

  span {
    font-weight: 500;
    color: #94a3b8;
  }

  &.is-up {
    color: #16a34a;
  }

  &.is-down {
    color: #dc2626;
  }
}
</style>
